import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import SearchSelect from '../components/SearchSelect';
import { inr, num, apiError } from '../utils/format';
import { packsOf } from '../utils/packs';
import { useAuth } from '../context/AuthContext';

const UNITS = ['kg', 'g', 'l', 'ml', 'piece'];
// Unit conversion (mirrors the API) so ingredient cost works across units.
const TO_BASE = { kg: 1000, g: 1, l: 1000, ml: 1, piece: 1 };
const FAMILY = { kg: 'mass', g: 'mass', l: 'vol', ml: 'vol', piece: 'count' };
const convert = (qty, from, to) => {
  const q = Number(qty) || 0;
  if (!from || !to || from === to) return q;
  if (FAMILY[from] && FAMILY[from] === FAMILY[to]) return (q * TO_BASE[from]) / TO_BASE[to];
  return q;
};
const emptyIng = () => ({ rawMaterialId: '', quantity: 0, unit: 'g' });
const empty = { makes: 'product', productId: '', rawMaterialId: '', name: '', baseYieldQty: 1, baseYieldUnit: 'kg', laborPct: 30, laborCost: 0, packingCost: 0, targetMarginPct: 40, notes: '', ingredients: [emptyIng()] };

export const RecipeModal = ({ recipe, products, materials, onClose, onSaved }) => {
  const [form, setForm] = useState(recipe
    ? { ...empty, ...recipe, makes: recipe.rawMaterialId ? 'raw' : 'product', productId: recipe.productId || '', rawMaterialId: recipe.rawMaterialId || '', ingredients: (recipe.ingredients || []).map((i) => ({ rawMaterialId: i.rawMaterialId, quantity: i.quantity, unit: i.unit })) }
    : empty);
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setIng = (i, k, v) => setForm((p) => ({ ...p, ingredients: p.ingredients.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const addIng = () => setForm((p) => ({ ...p, ingredients: [...p.ingredients, emptyIng()] }));
  const rmIng = (i) => setForm((p) => ({ ...p, ingredients: p.ingredients.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const raw = form.makes === 'raw';
      const payload = { ...form, productId: raw ? null : Number(form.productId), rawMaterialId: raw ? Number(form.rawMaterialId) : null, baseYieldQty: Number(form.baseYieldQty),
        laborPct: (form.laborPct != null && form.laborPct !== '') ? Number(form.laborPct) : null,   // blank → the fixed ₹ amount below
        laborCost: Number(form.laborCost) || 0, packingCost: Number(form.packingCost) || 0, targetMarginPct: Number(form.targetMarginPct) || 0,
        ingredients: form.ingredients.filter((i) => i.rawMaterialId).map((i) => ({ rawMaterialId: Number(i.rawMaterialId), quantity: Number(i.quantity), unit: i.unit })) };
      if (!payload.productId && !payload.rawMaterialId) throw new Error(raw ? 'Select the raw material this recipe makes' : 'Select a product');
      const { data } = recipe ? await api.put(`/recipes/${recipe.id}`, payload) : await api.post('/recipes', payload);
      toast.success('Saved'); onSaved(data.recipe); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={recipe ? 'Edit Recipe' : 'Add Recipe'} onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></>}>
      <form onSubmit={save}>
        <div className="row">
          {/* A recipe makes a product, or a raw material made from other raw materials (e.g. ghee from butter). */}
          <div className="field" style={{ flex: '0 0 150px' }}><label>Makes</label><select value={form.makes} onChange={(e) => set('makes', e.target.value)}><option value="product">Product</option><option value="raw">Raw material</option></select></div>
          {form.makes === 'raw' ? (
            <div className="field" style={{ flex: 2 }}><label>Raw material *</label><SearchSelect placeholder="Search raw material by name or ID…" value={form.rawMaterialId}
              onChange={(v) => { const m = materials.find((x) => x.id === Number(v)); setForm((p) => ({ ...p, rawMaterialId: v, baseYieldUnit: m?.unit || p.baseYieldUnit, name: p.name || m?.name || '' })); }}
              options={materials.map((m) => ({ value: m.id, label: `${m.name} (${m.unit})`, sub: `#${m.id}`, search: `${m.name} ${m.sku || ''} ${m.itemCode || ''} ${m.id}` }))} /></div>
          ) : (
            <div className="field" style={{ flex: 2 }}><label>Product *</label><SearchSelect placeholder="Search product by name or ID…" value={form.productId} onChange={(v) => set('productId', v)} options={products.map((p) => ({ value: p.id, label: p.name, sub: `#${p.id}`, search: `${p.name} ${p.code || ''} ${p.id}` }))} /></div>
          )}
          <div className="field" style={{ flex: 2 }}><label>Recipe name *</label><input value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
        </div>
        <div className="row">
          <div className="field"><label>Base yield</label><input type="number" step="0.001" value={form.baseYieldQty} onChange={(e) => set('baseYieldQty', e.target.value)} /></div>
          <div className="field"><label>Yield unit</label><select value={form.baseYieldUnit} onChange={(e) => set('baseYieldUnit', e.target.value)}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select></div>
          <div className="field"><label>Labor % <span className="muted small">(of material)</span></label><input type="number" step="0.01" min="0" value={form.laborPct ?? ''} onChange={(e) => set('laborPct', e.target.value)} placeholder="blank = fixed ₹" /></div>
          {!(form.laborPct != null && form.laborPct !== '') && <div className="field"><label>Labor ₹ / yield</label><input type="number" step="0.01" value={form.laborCost} onChange={(e) => set('laborCost', e.target.value)} /></div>}
          <div className="field"><label>Packing cost / yield</label><input type="number" step="0.01" value={form.packingCost} onChange={(e) => set('packingCost', e.target.value)} /></div>
          <div className="field"><label>Target margin %</label><input type="number" step="0.01" value={form.targetMarginPct} onChange={(e) => set('targetMarginPct', e.target.value)} /></div>
        </div>

        <div className="section-head" style={{ marginTop: '.4rem' }}><h3 style={{ fontSize: '.95rem' }}>Ingredients (per base yield)</h3><button type="button" className="btn btn-outline btn-sm" onClick={addIng}>+ Add</button></div>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Raw material</th><th>Quantity</th><th>Unit</th><th className="right">Cost</th><th></th></tr></thead>
            <tbody>
              {form.ingredients.map((ing, i) => {
                const mat = materials.find((m) => m.id === Number(ing.rawMaterialId));
                const cost = mat ? convert(ing.quantity, ing.unit, mat.unit) * Number(mat.purchasePrice || 0) : 0;
                return (
                <tr key={i}>
                  <td><select value={ing.rawMaterialId} onChange={(e) => setIng(i, 'rawMaterialId', e.target.value)}><option value="">— select —</option>{materials.filter((m) => !(form.makes === 'raw' && m.id === Number(form.rawMaterialId))).map((m) => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}</select></td>
                  <td><input type="number" step="0.001" style={{ width: 110 }} value={ing.quantity} onChange={(e) => setIng(i, 'quantity', e.target.value)} /></td>
                  <td><select value={ing.unit} onChange={(e) => setIng(i, 'unit', e.target.value)}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select></td>
                  <td className="right">{mat ? inr(cost) : '—'}</td>
                  <td><button type="button" className="btn btn-danger btn-sm" onClick={() => rmIng(i)}>✕</button></td>
                </tr>
                );
              })}
            </tbody>
            <tfoot><tr>
              <td colSpan={3} className="right"><strong>Material cost</strong></td>
              <td className="right"><strong>{inr(form.ingredients.reduce((s, ing) => { const mat = materials.find((m) => m.id === Number(ing.rawMaterialId)); return s + (mat ? convert(ing.quantity, ing.unit, mat.unit) * Number(mat.purchasePrice || 0) : 0); }, 0))}</strong></td>
              <td></td>
            </tr></tfoot>
          </table>
        </div>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Modal>
  );
};

const CostingModal = ({ recipe, products, onClose }) => {
  const product = products.find((p) => p.id === recipe.productId);
  const variants = packsOf(product);
  const [variantId, setVariantId] = useState(variants[0]?.id || '');
  const [units, setUnits] = useState(100);
  const [kg, setKg] = useState(1);
  const [data, setData] = useState(null);
  const byWeight = variants.length === 0;   // loose / no packs → cost per kg
  const qtyUnit = recipe.output?.unit || 'kg';   // a made raw material is costed in its own unit

  useEffect(() => {
    if (byWeight ? !(kg > 0) : !variantId) return;
    const q = byWeight ? `qty=${kg}&unit=${qtyUnit}` : `units=${units}&variantId=${variantId}`;
    api.get(`/recipes/${recipe.id}/costing?${q}`).then(({ data }) => setData(data.costing)).catch((e) => toast.error(apiError(e)));
  }, [recipe.id, variantId, units, kg, byWeight]);

  return (
    <Modal title={`Costing — ${recipe.name}`} onClose={onClose} footer={<button className="btn btn-primary" onClick={onClose}>Close</button>}>
      {byWeight ? (
        <div className="row">
          <div className="field"><label>Quantity to produce ({qtyUnit})</label><input type="number" min="0" step="0.001" value={kg} onChange={(e) => setKg(Number(e.target.value) || 0)} /></div>
        </div>
      ) : (
      <div className="row">
        <div className="field"><label>Variant</label><select value={variantId} onChange={(e) => setVariantId(e.target.value)}>{variants.map((v) => <option key={v.id} value={v.id}>{v.weightLabel}</option>)}</select></div>
        <div className="field"><label>Units to produce</label><input type="number" value={units} onChange={(e) => setUnits(Number(e.target.value) || 0)} /></div>
      </div>
      )}
      {data ? (
        <div className="card card-pad mt">
          <div className="bill-row"><span>Output</span><span>{num(data.outputKg)} {byWeight ? qtyUnit : 'kg'}{data.units ? ` (${data.units} units)` : ''}</span></div>
          <div className="bill-row"><span>Material cost</span><span>{inr(data.materialCost)}</span></div>
          <div className="bill-row"><span>Labor cost</span><span>{inr(data.laborCost)}</span></div>
          <div className="bill-row"><span>Packing cost</span><span>{inr(data.packingCost)}</span></div>
          <div className="bill-row total"><span>Total cost</span><span>{inr(data.totalCost)}</span></div>
          <div className="bill-row"><span>Cost / {byWeight ? qtyUnit : 'unit'}</span><span>{inr(data.costPerUnit)}</span></div>
          <div className="bill-row"><span>Suggested price</span><span>{inr(data.suggestedPrice)}</span></div>
          <div className="bill-row"><span>Current selling price</span><span>{data.sellingPrice != null ? inr(data.sellingPrice) : '—'}</span></div>
          <div className="bill-row"><span>Actual margin</span><span>{data.actualMarginPct != null ? `${data.actualMarginPct}%` : '—'}</span></div>
        </div>
      ) : <div className="spinner" />}
    </Modal>
  );
};

const Recipes = () => {
  const { can } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);
  const [costing, setCosting] = useState(null);

  const load = () => { setLoading(true); api.get('/recipes?all=1').then(({ data }) => setRecipes(data.recipes || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get('/products').then(({ data }) => setProducts(data.products || [])); api.get('/raw-materials').then(({ data }) => setMaterials(data.materials || [])); }, []);
  const onSaved = () => load();

  return (
    <div>
      <div className="section-head">
        <span className="muted small">{recipes.length} recipes</span>
        {can('recipes:write') && <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Add Recipe</button>}
      </div>
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Recipe</th><th>Makes</th><th className="right">Base yield</th><th className="right">Material/kg</th><th className="right">Cost/kg</th><th className="right">Margin%</th><th className="right">Suggested/kg</th><th className="right">Current MRP/kg</th><th></th></tr></thead>
            <tbody>
              {recipes.map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/recipes/${r.id}`} className="link-strong">{r.name}</Link></td>
                  <td>{r.product?.name || (r.output ? <>{r.output.name} <span className="badge badge-gray">raw material</span></> : '—')}</td>
                  <td className="right">{num(r.baseYieldQty)} {r.baseYieldUnit}</td>
                  <td className="right">{inr(r.materialCostPerKg)}</td>
                  <td className="right">{inr(r.costPerKg)}</td>
                  <td className="right">{Number(r.targetMarginPct)}%</td>
                  <td className="right">{inr(r.suggestedPricePerKg)}</td>
                  <td className="right">{r.currentMrpPerKg != null ? (r.currentMrpPerKg === r.currentMrpPerKgMax ? inr(r.currentMrpPerKg) : `${inr(r.currentMrpPerKg)}–${inr(r.currentMrpPerKgMax)}`) : '—'}</td>
                  <td>
                    <div className="flex gap">
                      <button className="btn btn-outline btn-sm" onClick={() => setCosting(r)}>Costing</button>
                      {can('recipes:write') && <button className="btn btn-outline btn-sm" onClick={() => setEditing(r)}>Edit</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {recipes.length === 0 && <tr><td colSpan={9} className="empty">No recipes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {editing !== undefined && <RecipeModal recipe={editing} products={products} materials={materials} onClose={() => setEditing(undefined)} onSaved={onSaved} />}
      {costing && <CostingModal recipe={costing} products={products} onClose={() => setCosting(null)} />}
    </div>
  );
};

export default Recipes;
